import React, { useEffect } from "react";
import { Typography, Paper, Grid, Box } from "@mui/material";
import { useClub } from "../../../../contexts/ClubContext";
import { TeamOverviewStats } from "../../../../types/LogTypes";
import { Club } from "../../../../types/ClubTypes";

interface Props {
  userClubStats?: TeamOverviewStats;
  opponentClubStats?: TeamOverviewStats | null;
  gamesPlayed?: number;
  userClub?: Club;
  opponentClub?: Club;
}

export const GameTeam: React.FC<Props> = ({
  userClubStats,
  opponentClubStats,
  gamesPlayed,
  userClub,
  opponentClub,
}) => {

  if (!userClubStats || !opponentClubStats || !gamesPlayed) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Paper
        sx={{ p: 3, bgcolor: "background.paper", maxWidth: 700, width: "100%" }}
      >
        <Grid container spacing={2} sx={{ maxWidth: 700 }}>
          {/* Club Name */}
          <Grid item xs={4}>
            <Typography variant="h6" sx={{ textAlign: "right" }}>
              {userClub?.name.replace("School", "")}
            </Typography>
          </Grid>
          {/* Stat Header */}
          <Grid item xs={4}>
            <Box sx={{ mb: 2 }}></Box>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h6" sx={{ textAlign: "left" }}>
              {opponentClub?.name.replace("School", "")}
            </Typography>
          </Grid>

          {/* Stats Rows */}
          <StatRow
            label="Points"
            team={userClubStats.points / gamesPlayed}
            opponent={opponentClubStats.points / gamesPlayed}
          />
          <StatRow
            label="Total Yards"
            team={userClubStats.total_yards / gamesPlayed}
            opponent={opponentClubStats.total_yards / gamesPlayed}
          />

          <StatRow
            label="Rushing Yards"
            team={userClubStats.rushing_yards / gamesPlayed}
            opponent={opponentClubStats.rushing_yards / gamesPlayed}
          />

          <StatRow
            label="Passing Yards"
            team={userClubStats.passing_yards / gamesPlayed}
            opponent={opponentClubStats.passing_yards / gamesPlayed}
          />

          <StatRow
            label="First Downs"
            team={userClubStats.first_downs / gamesPlayed}
            opponent={opponentClubStats.first_downs / gamesPlayed}
          />

          <StatRow
            label="Penalties"
            team={userClubStats.penalty_count / gamesPlayed}
            opponent={opponentClubStats.penalty_count / gamesPlayed}
            isHigherBetter={false}
          />

          <StatRow
            label="Penalty Yards"
            team={userClubStats.penalty_yards / gamesPlayed}
            opponent={opponentClubStats.penalty_yards / gamesPlayed}
            isHigherBetter={false}
          />
          <StatRow
            label="Turnovers"
            team={userClubStats.turnovers / gamesPlayed}
            opponent={opponentClubStats.turnovers / gamesPlayed}
            isHigherBetter={false}
          />

          <StatRow
            label="Takeaways"
            team={userClubStats.takeaways / gamesPlayed}
            opponent={opponentClubStats.takeaways / gamesPlayed}
            isHigherBetter={false}
          />

          <StatRow
            label="Third Down Conv"
            team={`${(userClubStats.third_downs_conv * 100).toFixed(0)}%`}
            opponent={`${(opponentClubStats.third_downs_conv * 100).toFixed(
              0
            )}%`}
          />

          <StatRow
            label="Fourth Down Conv"
            team={`${(userClubStats.fourth_downs_conv * 100).toFixed(0)}%`}
            opponent={`${(opponentClubStats.fourth_downs_conv * 100).toFixed(
              0
            )}%`}
          />

          <StatRow
            label="Rush Att"
            team={userClubStats.rushing_attempts / gamesPlayed}
            opponent={opponentClubStats.rushing_attempts / gamesPlayed}
          />

          <StatRow
            label="Avg. Yards / Rush"
            team={userClubStats.rushing_yards_per_attempt}
            opponent={opponentClubStats.rushing_yards_per_attempt}
          />

          <StatRow
            label="Pass Att"
            team={userClubStats.passing_attempts / gamesPlayed}
            opponent={opponentClubStats.passing_attempts / gamesPlayed}
          />

          <StatRow
            label="Avg. Yards / Pass"
            team={userClubStats.passing_yards_per_attempt}
            opponent={opponentClubStats.passing_yards_per_attempt}
          />

          <StatRow
            label="Tackle For Loss"
            team={userClubStats.tackles_for_loss / gamesPlayed}
            opponent={opponentClubStats.tackles_for_loss / gamesPlayed}
          />

          <StatRow
            label="Field Goal"
            team={`${userClubStats.field_goals_made}/${userClubStats.field_goal_attempts}`}
            opponent={`${opponentClubStats.field_goals_made}/${opponentClubStats.field_goal_attempts}`}
          />

          <StatRow
            label="PAT"
            team={`${userClubStats.pat_made}/${userClubStats.pat_attempts}`}
            opponent={`${opponentClubStats.pat_made}/${opponentClubStats.pat_attempts}`}
          />

          <StatRow
            label="2 Pt Conversion"
            team={`${userClubStats.two_point_conversions_made}/${userClubStats.two_point_conversion_attempts}`}
            opponent={`${opponentClubStats.two_point_conversions_made}/${opponentClubStats.two_point_conversion_attempts}`}
          />

          <StatRow
            label="Time of Possession"
            team={userClubStats.time_of_possession}
            opponent={opponentClubStats.time_of_possession}
          />
        </Grid>
      </Paper>
    </Box>
  );
};

interface StatRowProps {
  label: string;
  team: string | number;
  opponent: string | number;
  isHigherBetter?: boolean;
}

const StatRow: React.FC<StatRowProps> = ({
  label,
  team,
  opponent,
  isHigherBetter = true,
}) => {
  const teamValue = typeof team === "number" ? team.toFixed(1) : team;
  const opponentValue =
    typeof opponent === "number" ? opponent.toFixed(1) : opponent;

  return (
    <Grid container spacing={2} sx={{ maxWidth: 700 }}>
      <Grid
        item
        xs={4}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            textAlign: "right",
            fontWeight: "regular",
            color: "text.primary",
          }}
        >
          {teamValue}
        </Typography>
      </Grid>
      <Grid
        item
        xs={4}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            textAlign: "center",
            whiteSpace: "nowrap",
            lineHeight: 1.75,
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: "left",
            fontWeight: "regular",
            color: "text.primary",
          }}
        >
          {opponentValue}
        </Typography>
      </Grid>
    </Grid>
  );
};
